import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'core/ui/atoms';
import { Accordion } from 'core/ui/atoms/V2';
import { ReactComponent as CrossIcon } from 'core/assets/images/V2/icons/CrossIcon.svg';
import Money from 'core/api/Money';

const OrderItemList = ({ items, onDeleteItem, type = 'addon' }) => {
  const calculateTotalAmount = () => {
    const totalAmount = items.reduce((total, item) => {
      return total + (item.price.amount * (item.quantity || 1));
    }, 0);
    return {
      __typename: 'Money',
      amount: totalAmount,
    };
  };

  const RenderSwapItems = () => {
    return items.map((swapItem, index) => {
      const isCredit = swapItem?.price?.amount < 0;
      const displayAmount = isCredit
        ? `Credit ${Money({ __typename: 'Money', amount: Math.abs(swapItem?.price?.amount) }).toFormat('$0,0')}`
        : Money(swapItem?.price).toFormat('$0,0');

      return (
        <SwapItemContainer key={`swap-${index}-${swapItem?.id}`} direction='row' alignItems='flex-start'>
          <CrossIcon onClick={() => onDeleteItem([swapItem?.id])} />
          <Spacer />
          <TextContent direction='column'>
            <OriginalComponentName>{swapItem?.originalComponentName}</OriginalComponentName>
            <ReplacedNameRow direction='row' justifyContent='space-between' alignItems='center'>
              <ReplacedComponentName>{swapItem?.replacedComponentName}</ReplacedComponentName>
              <AmountText isCredit={isCredit}>{displayAmount}</AmountText>
            </ReplacedNameRow>
          </TextContent>
        </SwapItemContainer>
      );
    });
  };

  const renderItem = (item, index) => {
    if (type === 'addon') {
      return (
        <ItemContainer key={index} direction='row' justifyContent='space-between'>
          <Flex direction='row'>
            <CrossIcon onClick={() => onDeleteItem([item?.id])} />
            <Spacer />
            <Flex direction='column'>
              <Title>{item?.originalComponentName}</Title>
              <SubTitle>Quantity: {item?.quantity}</SubTitle>
            </Flex>
          </Flex>
          <Title>{Money(item?.price?.amount * item?.quantity).toFormat('$0,0') || 0}</Title>
        </ItemContainer>
      );
    }

    if (type === 'swap') {
      const amount = calculateTotalAmount();
      const swapIds = items.map((swap) => swap.id);
      if (index === 0) {
        return (
          <SwapItemRow direction='row' justifyContent='space-between' alignItems='flex-start'>
            <AccordionWrapper>
              <Accordion key={`order-${swapIds}`}
                subMenus={[]}
                subMenuIcon='caret'
                content={<RenderSwapItems />}>
                <AccordionTitleContent>
                  <CrossIcon onClick={() => onDeleteItem(swapIds)} />
                  <Spacer />
                  <Flex direction='column'>
                    <Title>Signature Program - Meal Edits  (Qty {items?.length})</Title>
                    <SubTitle>Breakfast + Lunch + Dinner</SubTitle>
                  </Flex>
                </AccordionTitleContent>
              </Accordion>
            </AccordionWrapper>
            <MainAccordionPrice>{Money(amount).toFormat('$0,0') || 0}</MainAccordionPrice>
          </SwapItemRow>
        );
      }
      return null;
    }

    return null;
  };

  return (
    <>
      {items.map((item, index) => renderItem(item, index))}
    </>
  );
};

OrderItemList.propTypes = {
  items: PropTypes.array.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['addon', 'swap']).isRequired,
};

const SwapItemRow = styled(Flex)`
  width: 100%;
  align-items: flex-start;
  column-gap: 20px;
  ${(p) => p.theme.max('md')`
    width: 100%;
    align-items: flex-start;
    column-gap: normal;
  `}
`;

const AccordionWrapper = styled.div`
  width: 100%;
`;

const AccordionTitleContent = styled(Flex)`
  flex-direction: row;
  align-items: center;
  padding-left: 15px;
  padding-right: 15px;
  min-height: 60px;
`;

const MainAccordionPrice = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  margin: 0px;
  margin-right: 15px;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  flex-shrink: 0;
`;

const ItemContainer = styled(Flex)`
  padding-left: 15px;
  padding-right: 15px;
  width: 100%;
`;

const Spacer = styled(Box)`
  width: 10px;
`;

const Title = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  margin: 0px;
  font-style: italic;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
`;

const SubTitle = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  margin: 0px;
  font-style: normal;
  line-height: normal;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyPoppins};
`;

const SwapItemContainer = styled(Flex)`
  padding-left: 40px;
  padding-right: 15px;
  width: 100%;
  margin-bottom: 8px;
`;

const TextContent = styled(Flex)`
  flex: 1;
`;

const ReplacedNameRow = styled(Flex)`
  width: 100%;
`;

const OriginalComponentName = styled(Text)`
  font-weight: 400;
  line-height: 22px;
  margin: 0px;
  color: #A7B2A3;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  text-decoration: line-through;
  opacity: 0.7;
  font-size: 14px;
  font-style: normal;
  text-decoration-line: line-through;
`;

const ReplacedComponentName = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  margin: 0px;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
`;

const AmountText = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  margin: 0px;
  color: ${(p) => p.isCredit ? p.theme.colors.green?.medium || p.theme.colors.primaryV2 : p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  flex-shrink: 0;
`;

export default OrderItemList;
